import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EMAIL_REG_EXP } from "src/domains/shared/constants/constants";
import type { useProjectOwnerTransfer } from "./useProjectOwnerTransfer";

/** 이 폼이 읽고 부르는 `useProjectOwnerTransfer` 반환 — 창 열림·서버 실패 키·진행 상태와 그 처리기. */
type TransferOwnerSlice = ReturnType<typeof useProjectOwnerTransfer>;

/**
 * 소유권 이전 창의 입력 폼 — 이메일 입력과, 창에 보일 오류 문구 하나(두 출처 중 하나)를 여기서 정한다.
 *
 * 오류 문구의 출처는 둘이다: 이 폼이 정하는 형식 오류(빈 값·형식)와 `useProjectOwnerTransfer` 가 들고 있는 서버 실패
 * 키(`transferError`). 서버 실패 키를 채우는 일과 열기·닫기·실행 때 비우는 일은 그 훅의 처리기
 * (`handleTransferOwner`·`handleOpenTransferDialog`·`handleCloseTransferDialog`) 몫이고, 이 폼이 직접 비우는
 * 때는 입력을 고칠 때(`clearTransferError`)뿐이다.
 *
 * `openDialog` 는 머리의 소유권 이전 버튼에, `dialogProps` 는 `TransferOwnerDialog` 에 그대로 펼쳐 넘긴다.
 */
export const useTransferOwnerForm = (transfer: TransferOwnerSlice) => {
  const { t } = useTranslation("projects");
  const [email, setEmail] = useState("");
  const [formatError, setFormatError] = useState<string | null>(null);

  // 보이는 오류는 하나 — 형식 오류가 먼저, 없으면 훅의 서버 실패 키를 문구로 바꾼다.
  const errorText =
    formatError ?? (transfer.transferError ? t(transfer.transferError, { ns: "projects" }) : null);

  const openDialog = () => {
    setEmail("");
    setFormatError(null);
    transfer.handleOpenTransferDialog();
  };

  /** 이메일 형식 검사는 멤버 추가와 같은 규칙 — 빈 값·형식 오류는 요청 없이 창 안에 표시한다. */
  const resolveEmailFormatError = (value: string): string | null => {
    if (!value) return t("myPageModal.permission.emailRequired", { ns: "projects" });
    if (!EMAIL_REG_EXP.test(value)) return t("myPageModal.permission.emailInvalid", { ns: "projects" });
    return null;
  };

  const submit = () => {
    const trimmed = email.trim();
    const nextFormatError = resolveEmailFormatError(trimmed);
    if (nextFormatError) {
      setFormatError(nextFormatError);
      return;
    }
    transfer.handleTransferOwner({ email: trimmed });
  };

  // 형식 오류와 함께 서버 실패 문구도 비운다 — 창은 오류 문구가 있는 동안 실행을 막으므로,
  // 비우지 않으면 서버 실패 뒤에는 창을 닫았다 다시 열기 전까지 재시도할 수 없다.
  const changeEmail = (value: string) => {
    setEmail(value);
    setFormatError(null);
    if (transfer.transferError) transfer.clearTransferError();
  };

  return {
    openDialog,
    dialogProps: {
      open: transfer.showTransferDialog,
      email,
      errorText,
      transferring: transfer.isTransferring,
      onEmailChange: changeEmail,
      onSubmit: submit,
      onClose: transfer.handleCloseTransferDialog,
    },
  };
};
